(() => {
  if (customElements.get('bbw-hero-video')) return;

  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const MOBILE = '(max-width: 749px)';
  const IDLE_DELAY_MS = 400;

  class BbwHeroVideo extends HTMLElement {
    constructor() {
      super();
      this.connected = false;
      this.gatesReady = false;
      this.inView = false;
      this.userPaused = false;
      this.hydrated = false;
      this.selectedSrc = '';
      this.hasPlayed = false;
      this.playbackToken = 0;
      this.idleId = null;
      this.gateTimer = null;
      this.observer = null;
      this.video = null;
      this.control = null;
      this.controlLabel = null;
      this.reducedMotion = window.matchMedia(REDUCED_MOTION);

      this.handleWindowLoad = this.handleWindowLoad.bind(this);
      this.handleIntersection = this.handleIntersection.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleReducedMotionChange = this.handleReducedMotionChange.bind(this);
      this.handlePlaying = this.handlePlaying.bind(this);
      this.handleError = this.handleError.bind(this);
      this.handleControlClick = this.handleControlClick.bind(this);
    }

    connectedCallback() {
      if (this.connected) return;

      this.connected = true;
      this.video = this.querySelector('[data-bbw-hero-video]');
      this.control = this.parentElement?.querySelector('[data-bbw-hero-motion]') || null;
      this.controlLabel = this.control?.querySelector('[data-bbw-hero-motion-label]') || null;
      this.selectSourceOnce();
      this.syncControl();

      if (!this.video || !this.selectedSrc || this.shouldBlockMedia()) return;

      this.video.addEventListener('playing', this.handlePlaying);
      this.video.addEventListener('error', this.handleError);
      this.control?.addEventListener('click', this.handleControlClick);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      if (this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', this.handleReducedMotionChange);
      } else {
        this.reducedMotion.addListener(this.handleReducedMotionChange);
      }

      if (typeof IntersectionObserver === 'function') {
        this.observer = new IntersectionObserver(this.handleIntersection);
        this.observer.observe(this);
      }

      this.scheduleGates();
    }

    disconnectedCallback() {
      if (!this.connected) return;

      this.connected = false;
      this.observer?.disconnect();
      this.observer = null;
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      if (this.reducedMotion.removeEventListener) {
        this.reducedMotion.removeEventListener('change', this.handleReducedMotionChange);
      } else {
        this.reducedMotion.removeListener(this.handleReducedMotionChange);
      }
      window.removeEventListener('load', this.handleWindowLoad);
      this.video?.removeEventListener('playing', this.handlePlaying);
      this.video?.removeEventListener('error', this.handleError);
      this.control?.removeEventListener('click', this.handleControlClick);

      if (this.idleId !== null && typeof cancelIdleCallback === 'function') cancelIdleCallback(this.idleId);
      if (this.gateTimer !== null) clearTimeout(this.gateTimer);
      this.idleId = null;
      this.gateTimer = null;
      this.playbackToken += 1;
      this.video?.pause();
      if (this.hydrated) {
        this.video?.removeAttribute('src');
        this.video?.load();
      }

      this.classList.remove('is-playing');
      this.gatesReady = false;
      this.inView = false;
      this.userPaused = false;
      this.hydrated = false;
      this.selectedSrc = '';
      this.hasPlayed = false;
      this.syncControl();
    }

    selectSourceOnce() {
      if (this.selectedSrc) return this.selectedSrc;
      const isMobile = window.matchMedia(MOBILE).matches;
      this.selectedSrc = isMobile ? this.getAttribute('data-mobile-src') || '' : this.getAttribute('data-desktop-src') || '';
      return this.selectedSrc;
    }

    scheduleGates() {
      if (document.readyState === 'complete') {
        this.handleWindowLoad();
      } else {
        window.addEventListener('load', this.handleWindowLoad, { once: true });
      }
    }

    handleWindowLoad() {
      if (!this.connected || this.gatesReady || this.idleId !== null || this.gateTimer !== null) return;
      const waitForIdle = () => {
        this.idleId = null;
        this.gateTimer = setTimeout(() => {
          this.gateTimer = null;
          this.gatesReady = true;
          this.maybeHydrateAndPlay();
        }, IDLE_DELAY_MS);
      };
      this.idleId = typeof requestIdleCallback === 'function' ? requestIdleCallback(waitForIdle) : null;
      if (this.idleId === null) waitForIdle();
    }

    maybeHydrateAndPlay() {
      if (!this.connected || !this.gatesReady || !this.inView || this.shouldBlockMedia()) return;
      if (!this.hydrated) this.hydrate();
      this.play();
    }

    hydrate() {
      if (!this.video || !this.selectedSrc || this.hydrated) return;
      this.video.muted = true;
      this.video.setAttribute('src', this.selectedSrc);
      this.video.load();
      this.hydrated = true;
    }

    play() {
      if (!this.connected || !this.hydrated || !this.inView || this.userPaused || this.shouldBlockMedia() || document.visibilityState === 'hidden') return;
      const token = ++this.playbackToken;
      const result = this.video?.play();
      if (result?.catch) {
        result.catch(() => {
          if (this.connected && token === this.playbackToken) this.showFallback();
        });
      }
    }

    pause(userInitiated = false) {
      this.playbackToken += 1;
      if (userInitiated) this.userPaused = true;
      this.video?.pause();
      this.classList.remove('is-playing');
      this.syncControl();
    }

    showFallback() {
      this.playbackToken += 1;
      this.video?.pause();
      this.classList.remove('is-playing');
      this.syncControl();
    }

    syncControl() {
      if (!this.control) return;
      this.control.hidden = !this.hasPlayed;
      const isPlaying = this.isPlaying();
      const state = isPlaying ? 'pause' : 'play';
      const label = isPlaying ? 'Pause background animation' : 'Play background animation';
      this.control.setAttribute('data-motion-state', state);
      this.control.setAttribute('aria-label', label);
      if (this.controlLabel) this.controlLabel.textContent = label;
    }

    isPlaying() {
      return this.classList.contains('is-playing');
    }

    shouldBlockMedia() {
      return this.reducedMotion.matches || navigator.connection?.saveData;
    }

    handleIntersection(entries) {
      this.inView = entries.some((entry) => entry.isIntersecting);
      if (this.inView) {
        this.maybeHydrateAndPlay();
      } else {
        this.pause();
      }
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.pause();
      } else {
        this.play();
      }
    }

    handleReducedMotionChange() {
      if (this.reducedMotion.matches) {
        this.showFallback();
      } else {
        this.play();
      }
    }

    handlePlaying() {
      if (!this.connected || !this.hydrated || !this.inView || this.shouldBlockMedia() || this.userPaused || document.visibilityState === 'hidden' || this.video?.paused) {
        this.showFallback();
        return;
      }
      this.hasPlayed = true;
      this.classList.add('is-playing');
      this.syncControl();
    }

    handleError() {
      this.showFallback();
    }

    handleControlClick() {
      if (this.isPlaying()) {
        this.pause(true);
      } else {
        this.userPaused = false;
        this.syncControl();
        this.play();
      }
    }
  }

  customElements.define('bbw-hero-video', BbwHeroVideo);
})();
